import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { resolveImage } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import { Instagram, Facebook, MessageCircle, Globe, X as XIcon } from "lucide-react";

const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  whatsapp: MessageCircle,
  tiktok: Globe,
  xhs: Globe,
  wechat: Globe,
  other: Globe,
};

const PLATFORM_COLORS = {
  instagram: "hover:text-[#E1306C]",
  facebook: "hover:text-[#1877F2]",
  whatsapp: "hover:text-[#25D366]",
  tiktok: "hover:text-black",
  xhs: "hover:text-[#FF2442]",
  wechat: "hover:text-[#07C160]",
  other: "hover:text-[#00868e]",
};

export default function Angelina() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [content, setContent] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const pt = usePageTitle("angelina", {
    title_zh: t("angelina.title"), title_en: t("angelina.title"),
    subtitle_zh: t("angelina.subtitle"), subtitle_en: t("angelina.subtitle"),
  });

  useEffect(() => {
    api.get("/angelina").then((r) => setContent(r.data));
  }, []);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxIdx(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx]);

  if (!content) return <div className="p-16 text-[#4a6e77]">{t("common.loading")}</div>;

  const photos = content.photos || [];
  const mainPhoto = photos[0];
  const galleryPhotos = photos.slice(1);
  const socialLinks = content.social_links || [];

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16" data-testid="angelina-page">
      <div className="grid md:grid-cols-[2fr,3fr] gap-12 items-start">
        <div>
          {mainPhoto && (
            <div className="aspect-[4/5] rounded-md overflow-hidden bg-[#f5f0e8]" data-testid="angelina-photo">
              <img src={resolveImage(mainPhoto)} alt="Angelina" className="h-full w-full object-cover" />
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="angelina-subtitle">{zh ? pt.subtitle_zh : pt.subtitle_en}</div>
          <h1 className="mt-3 font-serif-cn text-4xl md:text-5xl text-[#003645] font-semibold tracking-tight" data-testid="angelina-title">
            {zh ? pt.title_zh : pt.title_en}
          </h1>
          <div className="mt-8 font-serif-cn text-lg leading-[1.9] text-[#4a6e77] whitespace-pre-wrap" data-testid="angelina-bio">
            {(zh ? content.bio_zh : content.bio_en) || (
              <span className="italic text-[#4a6e77]/70">
                {zh ? "内容即将上线，敬请期待。" : "Content coming soon."}
              </span>
            )}
          </div>

          {socialLinks.length > 0 && (
            <div className="mt-10" data-testid="angelina-social">
              <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
                {t("social.title")}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {socialLinks.map((s, i) => {
                  const Icon = PLATFORM_ICONS[s.platform] || Globe;
                  const colorCls = PLATFORM_COLORS[s.platform] || "hover:text-[#00868e]";
                  return (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e5ebeb] text-sm text-[#003645] hover:border-current transition-colors duration-200 ${colorCls}`}
                      data-testid={`social-link-${s.platform}`}
                    >
                      <Icon size={14} />
                      <span>{s.label || t(`social.platforms.${s.platform}`) || s.platform}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo gallery */}
      {galleryPhotos.length > 0 && (
        <section className="mt-20" data-testid="angelina-gallery">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">gallery</div>
          <h2 className="mt-3 font-serif-cn text-2xl text-[#003645] font-semibold">{zh ? "灵性时刻" : "Moments"}</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryPhotos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxIdx(i + 1)}
                className="group relative aspect-square rounded-md overflow-hidden bg-[#f5f0e8] focus:outline-none focus:ring-2 focus:ring-[#00868e]"
                data-testid={`angelina-gallery-item-${i}`}
              >
                <img src={resolveImage(p)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-[#003645]/0 group-hover:bg-[#003645]/20 transition-colors duration-300" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center p-6 z-50"
          onClick={() => setLightboxIdx(null)}
          data-testid="angelina-lightbox"
        >
          <button aria-label="close" data-testid="angelina-lightbox-close" className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setLightboxIdx(null)}>
            <XIcon size={28} />
          </button>
          <img src={resolveImage(photos[lightboxIdx])} alt="" className="max-h-[90vh] max-w-full rounded-md" />
        </div>
      )}

      {(content.products || []).length > 0 && (
        <section className="mt-20" data-testid="angelina-recommends">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
            recommends
          </div>
          <h2 className="mt-3 font-serif-cn text-3xl text-[#003645] font-semibold">
            {t("angelina.recommends")}
          </h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {content.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
